/**
 * Vänbjudan som överlever inloggningen.
 *
 * Mottagaren öppnar en länk, är utloggad, registrerar sig — och ska landa
 * tillbaka på bjudan i stället för på startsidan med en oförklarad känsla
 * av att något försvann. Registreringen laddar om sidan och byter rutt, så
 * en state-variabel eller ett router-state räcker inte: token måste
 * överleva en full omladdning.
 *
 * localStorage och inte sessionStorage: delas länken i en app öppnas den
 * ofta i en ny flik, och en verifieringslänk i mailen kan öppnas i en
 * annan flik igen.
 *
 * Bara token sparas. Den ger i sig ingen åtkomst till något — den leder
 * till en följarförfrågan som fortfarande måste godkännas — och inga
 * uppgifter om den som bjudit in lagras på mottagarens enhet.
 */

const KEY = "pending_friend_invite";

/** Efter det här är det inte längre ett avbrutet flöde utan en gammal länk. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

type Stored = { token: string; savedAt: number };

export function rememberPendingInvite(token: string) {
  if (!token) return;

  try {
    const value: Stored = { token, savedAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Privat läge eller full disk. Flödet fungerar ändå, mottagaren får
    // bara öppna länken igen efter inloggningen.
  }
}

export function takePendingInvite(): string | null {
  let raw: string | null = null;

  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return null;
  }

  clearPendingInvite();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed?.token) return null;
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) return null;

    return parsed.token;
  } catch {
    return null;
  }
}

export function clearPendingInvite() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Se ovan.
  }
}

/**
 * Vart en användare ska skickas efter inloggning eller registrering.
 *
 * Anropas av Login och Register i stället för att navigera rakt till sin
 * vanliga destination. Finns ingen väntande bjudan svarar den null och
 * sidorna gör som förut.
 */
export function pendingInviteRoute(): string | null {
  const token = takePendingInvite();
  return token ? `/invite/${encodeURIComponent(token)}` : null;
}
